import { create } from "zustand";
import { whatsappApi } from "@services/api/organizations";
import type { WhatsAppSessionStatus } from "@services/api/organizations";

/**
 * Live WhatsApp session health, shared by every consumer.
 *
 * Why a store and not a hook per component: the global banner, the chat composer
 * and the settings page all need this at the same time. A hook per component would
 * poll WasenderAPI once per mount — one store means one interval for the whole app.
 *
 * `connected === false && critical === true` means the session is dead and will NOT
 * recover on its own: outgoing messages fail (they queue and auto-resend) and, worse,
 * INCOMING client messages never arrive at all. That is what the UI must shout about.
 */

const POLL_MS = 60_000;

type WhatsAppStatusState = {
  status: WhatsAppSessionStatus | null;
  loading: boolean;
  /** Set when the status call itself fails — we then say nothing rather than lie. */
  unreachable: boolean;
  fetch: (force?: boolean) => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
};

let pollTimer: ReturnType<typeof setInterval> | null = null;
let subscribers = 0;

export const useWhatsAppStatusStore = create<WhatsAppStatusState>((set) => ({
  status: null,
  loading: false,
  unreachable: false,

  fetch: async (force = false) => {
    set({ loading: true });
    try {
      const status = await whatsappApi.getSessionStatus(force);
      set({ status, loading: false, unreachable: false });
    } catch {
      // Keep the last known status: a failed poll is not evidence of health.
      set({ loading: false, unreachable: true });
    }
  },

  startPolling: () => {
    subscribers += 1;
    const { fetch } = useWhatsAppStatusStore.getState();
    fetch();
    if (!pollTimer) {
      pollTimer = setInterval(() => {
        // Skip polling a hidden tab; refresh immediately when it comes back.
        if (document.visibilityState === "visible") fetch();
      }, POLL_MS);
    }
  },

  stopPolling: () => {
    subscribers = Math.max(0, subscribers - 1);
    if (subscribers === 0 && pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  },
}));

/** True when WhatsApp cannot currently send or receive — used to block the composer. */
export function isWhatsAppDown(status: WhatsAppSessionStatus | null): boolean {
  if (!status) return false; // unknown ≠ down; never block on missing data
  return !status.connected;
}

export type WhatsAppBlock = { blocked: boolean; reason: "disabled" | "disconnected" | null };

/**
 * Why WhatsApp sending is unavailable, if it is.
 *
 * Two causes that must not be conflated: `disabled` is the org's own kill switch
 * (deliberate — say so calmly), `disconnected` is a broken session (a failure —
 * shout, and offer the QR). Sending is blocked server-side in both cases, so the
 * UI's job is only to explain which one it is before someone types a message.
 */
export function whatsAppBlock(status: WhatsAppSessionStatus | null): WhatsAppBlock {
  if (!status) return { blocked: false, reason: null };
  if (status.sending_enabled === false) return { blocked: true, reason: "disabled" };
  if (!status.connected) return { blocked: true, reason: "disconnected" };
  return { blocked: false, reason: null };
}
